import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, AlertTriangle, BellOff, ArrowRight } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { api } from '../../lib/api';
import type { AppNotification } from '../../lib/api';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

interface NotificationBellProps {
  onSelectJob: (jobId: string) => void;
}

export function NotificationBell({ onSelectJob }: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  // #113: the notification whose full detail is shown in the modal (null = closed).
  const [selected, setSelected] = useState<AppNotification | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Poll for unread notifications every 60s
  const { data, isPending, isError, isFetching, refetch } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api.notifications.list({ unreadOnly: true }),
    refetchInterval: 60_000,
  });

  const notifications = isError ? [] : (data?.data ?? []);
  const unreadCount = isError ? 0 : (data?.unreadCount ?? 0);

  async function refreshNotifications() {
    const result = await refetch();
    if (result.isSuccess) setReadError(null);
  }

  // Mark single notification as read
  const markReadMutation = useMutation({
    mutationFn: (id: string) => api.notifications.markRead(id),
    onMutate: () => setReadError(null),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: () => {
      setReadError(
        'Could not confirm that this notification was marked as read. Refresh notifications to check before trying again.',
      );
    },
  });

  // Mark all as read
  const markAllReadMutation = useMutation({
    mutationFn: () => api.notifications.markAllRead(),
    onMutate: () => setReadError(null),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: () => {
      setReadError(
        'Could not confirm that all notifications were marked as read. Refresh notifications to check before trying again.',
      );
    },
  });
  const readActionPending = markReadMutation.isPending || markAllReadMutation.isPending;

  // Close dropdown on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function jobIdOf(n: AppNotification): string | null {
    return (n.metadata?.jobId as string) || n.job_id || null;
  }

  // #113: clicking a row opens the full-detail modal instead of immediately
  // navigating — the dropdown truncates long messages (e.g. reassignment
  // conflicts listing staff/vehicles/equipment that couldn't transfer).
  function handleNotificationClick(n: AppNotification) {
    setSelected(n);
  }

  // Navigate to the related job from inside the detail modal.
  function handleGoToJob(n: AppNotification) {
    const jobId = jobIdOf(n);
    if (jobId) {
      onSelectJob(jobId);
      setSelected(null);
      setOpen(false);
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-md transition-colors"
        aria-label="Notifications"
        title={isError ? 'Notifications unavailable' : undefined}
      >
        {isError ? (
          <AlertTriangle className="w-4 h-4 text-amber-500" />
        ) : (
          <Bell className="w-4 h-4" />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-medium leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-neutral-200 rounded-lg shadow-lg z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-100">
            <span className="text-sm font-medium text-neutral-800">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllReadMutation.mutate()}
                className="text-[11px] text-blue-600 hover:text-blue-800 transition-colors"
                disabled={readActionPending || readError !== null}
              >
                Mark all as read
              </button>
            )}
          </div>

          {readError && (
            <div role="alert" className="px-3 py-2 text-xs text-red-700 bg-red-50">
              <p>{readError}</p>
              <button
                onClick={() => void refreshNotifications()}
                disabled={isFetching}
                className="mt-2 underline disabled:opacity-50"
              >
                Refresh notifications
              </button>
            </div>
          )}

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {isPending ? (
              <div role="status" className="px-3 py-8 text-center text-xs text-neutral-500">
                Loading notifications…
              </div>
            ) : isError ? (
              <div role="alert" className="px-3 py-6 text-center text-xs text-red-700">
                <p>Notifications are unavailable.</p>
                <button
                  onClick={() => void refreshNotifications()}
                  disabled={isFetching}
                  className="mt-2 underline disabled:opacity-50"
                >
                  Retry notifications
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-8 text-neutral-400">
                <BellOff className="w-6 h-6" />
                <span className="text-xs">No new notifications</span>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className="flex items-start gap-2 px-3 py-2.5 border-b border-neutral-50 hover:bg-neutral-50 transition-colors"
                >
                  {/* Icon */}
                  <div className="shrink-0 mt-0.5">
                    {n.severity === 'warning' ? (
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                    ) : (
                      <Bell className="w-4 h-4 text-neutral-400" />
                    )}
                  </div>

                  {/* Content — clickable; opens full-detail modal (#113) */}
                  <button
                    onClick={() => handleNotificationClick(n)}
                    className="flex-1 min-w-0 text-left"
                    aria-label={`View notification: ${n.title}`}
                  >
                    <div className="text-sm font-medium text-neutral-800 truncate">
                      {n.metadata?.ion ? `${n.metadata.ion} — ` : ''}
                      {n.title}
                    </div>
                    <div className="text-xs text-neutral-500 line-clamp-3 mt-0.5">{n.message}</div>
                    <div className="text-[10px] text-neutral-400 mt-1">
                      {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                    </div>
                  </button>

                  {/* Dismiss button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      markReadMutation.mutate(n.id);
                    }}
                    className="shrink-0 p-1 text-neutral-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                    aria-label="Mark as read"
                    disabled={readActionPending || readError !== null}
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* #113: full-detail modal — title + untruncated message + timestamp.
          Radix Dialog is dismissible via Esc / backdrop / the built-in close
          button (DialogContent renders its own aria-labelled Close). */}
      <Dialog open={selected !== null} onOpenChange={(o) => !o && setSelected(null)}>
        {selected && (
          <DialogContent className="max-w-md" aria-describedby="notification-detail-message">
            <DialogHeader>
              <DialogTitle className="flex items-start gap-2 pr-6 text-left">
                {selected.severity === 'warning' ? (
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
                ) : (
                  <Bell className="w-4 h-4 mt-0.5 shrink-0 text-neutral-400" />
                )}
                <span className="text-base font-medium text-neutral-800">
                  {selected.metadata?.ion ? `${selected.metadata.ion} — ` : ''}
                  {selected.title}
                </span>
              </DialogTitle>
            </DialogHeader>

            <p
              id="notification-detail-message"
              className="text-sm text-neutral-600 whitespace-pre-wrap break-words"
            >
              {selected.message}
            </p>

            <div className="text-xs text-neutral-400">
              {format(new Date(selected.created_at), 'd MMM yyyy, HH:mm')}
              {' · '}
              {formatDistanceToNow(new Date(selected.created_at), { addSuffix: true })}
            </div>

            <DialogFooter>
              {jobIdOf(selected) && (
                <button
                  onClick={() => handleGoToJob(selected)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#F26522] text-white rounded-md text-[13px] hover:bg-[#d9561d] transition-colors"
                >
                  Go to job
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
              <DialogClose asChild>
                <button className="px-3 py-1.5 text-[13px] text-neutral-600 border border-neutral-200 rounded-md hover:bg-neutral-50 transition-colors">
                  Dismiss
                </button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
